import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { useChannels } from "@/lib/feature-hooks";
import { toast } from "sonner";
import { Plus, BarChart3, Trash2 } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

export default function PollsPanel() {
  const { active } = useServer();
  const channels = useChannels(active?.id, active?.bot_present);
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ channel_id: "", question: "", options: ["", ""] });

  const refresh = () => {
    if (!active?.id || !active.bot_present) return;
    api.get(`/features/polls/${active.id}`).then((r) => setList(r.data));
  };
  useEffect(refresh, [active?.id, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;

  const create = async (e) => {
    e.preventDefault();
    const opts = form.options.map((o) => o.trim()).filter(Boolean);
    if (!form.channel_id || !form.question.trim() || opts.length < 2) {
      toast.error("Channel + question + at least 2 options");
      return;
    }
    try {
      await api.post(`/features/polls/${active.id}`, { ...form, options: opts });
      toast.success("Poll posted");
      setForm({ channel_id: "", question: "", options: ["", ""] });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="polls-panel" className="space-y-8">
      <div>
        <div className="overline mb-2"><BarChart3 className="inline w-4 h-4 mr-2 -mt-1" />UTILITIES</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Polls</h1>
        <p className="text-zinc-400 mt-2">Up to 10 options. Members vote with reactions.</p>
      </div>

      <form onSubmit={create} className="tactical-card p-6 space-y-4" data-testid="poll-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="overline block mb-2">Channel</label>
            <select className="tactical-input" value={form.channel_id} onChange={(e) => setForm({ ...form, channel_id: e.target.value })} data-testid="poll-channel">
              <option value="">— Select —</option>
              {channels.filter((c) => c.type === "text").map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-2">Question</label>
            <input className="tactical-input" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="poll-question" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="overline">Options</div>
          {form.options.map((opt, i) => (
            <div key={i} className="flex gap-2">
              <input className="tactical-input" placeholder={`Option ${i + 1}`} value={opt} onChange={(e) => {
                const o = [...form.options]; o[i] = e.target.value; setForm({ ...form, options: o });
              }} data-testid={`poll-opt-${i}`} />
              {form.options.length > 2 && (
                <button type="button" onClick={() => setForm({ ...form, options: form.options.filter((_, j) => j !== i) })} className="btn-ghost border border-brand-line"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
          {form.options.length < 10 && (
            <button type="button" onClick={() => setForm({ ...form, options: [...form.options, ""] })} className="btn-secondary text-sm">
              <Plus className="w-4 h-4" /> Add option
            </button>
          )}
        </div>

        <div className="flex justify-end">
          <button type="submit" className="btn-primary" data-testid="poll-create-btn"><Plus className="w-4 h-4" /> Post Poll</button>
        </div>
      </form>

      <div className="tactical-card p-6">
        <h2 className="font-heading text-xl font-bold uppercase mb-4">Recent Polls ({list.length})</h2>
        <div className="space-y-2">
          {list.length === 0 && <div className="text-zinc-500 font-mono-tabular text-sm py-6 text-center">No polls yet</div>}
          {list.map((p) => (
            <div key={p.id} className="border border-brand-line p-3 rounded-lg" data-testid={`poll-row-${p.id}`}>
              <div className="text-sm font-semibold">{p.question}</div>
              <div className="text-xs text-zinc-500 mt-1">{p.options.length} options · {new Date(p.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
